import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { Chip, Tooltip } from "@heroui/react";
import { Link } from "react-router";
import { useEffect, useState } from "react";

interface DomainParts {
    domain: string | null;
    domainWithoutSuffix: string | null;
}

const NO_DOMAIN: DomainParts = { domain: null, domainWithoutSuffix: null };

interface RecipeSourceChipProps {
    readonly sourceUrl: string;
}

export const RecipeSourceChip = ({ sourceUrl }: RecipeSourceChipProps) => {
    // `tldts` bundles the full Public Suffix List (~110 KB gzip), so we load it
    // lazily: the chip renders immediately with the globe fallback, then swaps
    // in the parsed domain once the library resolves.
    const [sourceUrlDomain, setSourceUrlDomain] = useState<DomainParts>(NO_DOMAIN);

    useEffect(() => {
        let active = true;

        let hostname: string;
        try {
            hostname = new URL(sourceUrl).hostname;
        } catch {
            setSourceUrlDomain(NO_DOMAIN);
            return;
        }

        import("tldts").then(({ parse }) => {
            if (!active) return;
            const { domain, domainWithoutSuffix } = parse(hostname);
            setSourceUrlDomain({ domain, domainWithoutSuffix });
        });

        return () => {
            active = false;
        };
    }, [sourceUrl]);

    return (
        <Tooltip>
            <Tooltip.Trigger>
                <Link to={sourceUrl} target="_blank" className="inline-flex">
                    <Chip size="lg">
                        {sourceUrlDomain.domain ? (
                            <img
                                src={`https://www.google.com/s2/favicons?domain=${sourceUrlDomain.domain}`}
                                alt="favicon"
                                className="h-4 w-4"
                            />
                        ) : (
                            <GlobeAltIcon className="h-4 w-4" />
                        )}
                        <Chip.Label>{sourceUrlDomain.domainWithoutSuffix ?? sourceUrlDomain.domain}</Chip.Label>
                    </Chip>
                </Link>
            </Tooltip.Trigger>
            <Tooltip.Content>
                <p>Recipe source: {sourceUrlDomain.domain}</p>
            </Tooltip.Content>
        </Tooltip>
    );
};
